/**
 * campaign-words.tsx — what the admin's campaign screens say a campaign's
 * status in: each word with its tone, from one table, so a status the API
 * types gain has both or the build fails. The campaign list draws a status as
 * a pill, and a campaign's page names it in its summary. A value off the wire
 * the table does not know — another casing among them — reads as itself, in
 * neutral. An activity's kind is the shell's ActivityKindPill, which the
 * student's views draw too.
 */

import { useTranslation } from 'react-i18next';
import { Pill, type PillTone } from '../../../components/pill';
import type { AdminCampaign } from '../../../api/admin/campaigns/types';

type Look = { key: string; tone: PillTone };

/**
 * Each status of a campaign's lifecycle, DRAFT → SCHEDULED → ACTIVE →
 * FINISHED: its words, a key written whole, and its tone. A draft is
 * `neutral`, not `locked`: --text-dim reads ~3.3:1 on a surface, and this is a
 * status label, as activeTone's inactive case is.
 */
const CAMPAIGN_STATUS_LOOK: Record<AdminCampaign['status'], Look> = {
  DRAFT: { key: 'admin.campaigns.status.DRAFT', tone: 'neutral' },
  SCHEDULED: { key: 'admin.campaigns.status.SCHEDULED', tone: 'info' },
  ACTIVE: { key: 'admin.campaigns.status.ACTIVE', tone: 'success' },
  FINISHED: { key: 'admin.campaigns.status.FINISHED', tone: 'neutral' },
};

/** A value's look, or none for one off the wire the table does not know. */
function lookOf<K extends string>(table: Record<K, Look>, value: K): Look | undefined {
  return (table as Partial<Record<string, Look>>)[value];
}

/** A campaign's status as a pill: the campaign list's. */
export function CampaignStatusPill({ status }: { status: AdminCampaign['status'] }) {
  const { t } = useTranslation();
  const look = lookOf(CAMPAIGN_STATUS_LOOK, status);
  return <Pill tone={look?.tone ?? 'neutral'}>{look ? t(look.key) : status}</Pill>;
}

/** A campaign's status in words: a campaign's page names it in its summary. */
export function CampaignStatusName({ status }: { status: AdminCampaign['status'] }) {
  const { t } = useTranslation();
  const look = lookOf(CAMPAIGN_STATUS_LOOK, status);
  return <>{look ? t(look.key) : status}</>;
}
